import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type Counterparty, type CounterpartyType, type NewCounterparty } from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from './Icons';

/**
 * Nowy kontrahent bez opuszczania faktury.
 *
 * Dotąd wystawianie faktury komuś nowemu znaczyło: porzuć szkic, przejdź do Kontrahentów, dodaj,
 * wróć, zacznij od nowa. Nikt nie pamięta, że szkic trzeba najpierw zapisać — więc pozycje
 * przepadały. Okienko rozwiązuje dokładnie to i nic więcej.
 *
 * Pola są **te same, co na ekranie Kontrahentów**, razem z pobieraniem z rejestru po NIP-ie.
 * Gdyby okienko miało własny, uproszczony formularz, powstałyby dwie drogi dodawania kontrahenta
 * różniące się tym, co zapisują — a różnicę zobaczyłby dopiero ktoś, kto szuka adresu na fakturze.
 */

export const COUNTERPARTY_TYPES: CounterpartyType[] = ['DOMESTIC', 'EU', 'NON_EU', 'PERSON'];

export const emptyCounterparty: NewCounterparty = {
  type: 'DOMESTIC',
  name: '',
  nip: '',
  vatEuId: '',
  country: 'PL',
  addressLine: '',
  postalCode: '',
  city: '',
  email: '',
  phone: '',
  notes: '',
  active: true,
};

/** Zmiana rodzaju kontrahenta zmienia to, co jest wymagane — czyścimy pola, które przestają pasować. */
export function withType(form: NewCounterparty, type: CounterpartyType): NewCounterparty {
  return {
    ...form,
    type,
    nip: type === 'DOMESTIC' ? form.nip : '',
    vatEuId: type === 'EU' ? form.vatEuId : '',
    country: type === 'DOMESTIC' || type === 'PERSON' ? 'PL' : form.country === 'PL' ? '' : form.country,
  };
}

/**
 * Pola kontrahenta — wspólne dla ekranu i okienka.
 *
 * `idPrefix` rozróżnia identyfikatory pól, bo okienko potrafi stanąć nad ekranem, który ma już
 * własny formularz; dwa `id="kNip"` na jednej stronie zerwałyby powiązanie etykiety z polem.
 */
export function CounterpartyFields({
  form,
  onChange,
  t,
  idPrefix = 'k',
  fetched,
  fetching,
  onFetch,
}: {
  form: NewCounterparty;
  onChange: (next: NewCounterparty) => void;
  t: Dictionary;
  idPrefix?: string;
  fetched: string | null;
  fetching: boolean;
  onFetch: () => void;
}) {
  const id = (name: string) => `${idPrefix}${name}`;

  return (
    <div className="fields">
      <div className="field">
        <label htmlFor={id('Type')}>{t.counterparties.type}</label>
        <select
          id={id('Type')}
          className="inp"
          value={form.type}
          onChange={(event) => onChange(withType(form, event.target.value as CounterpartyType))}
        >
          {COUNTERPARTY_TYPES.map((value) => (
            <option key={value} value={value}>
              {t.counterparties.types[value]}
            </option>
          ))}
        </select>
        <span className="hint">{t.counterparties.typeHint}</span>
      </div>
      {form.type === 'DOMESTIC' && (
        <div className="field">
          <label htmlFor={id('Nip')}>{t.counterparties.nip}</label>
          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <input
              id={id('Nip')}
              className="inp mono"
              value={form.nip ?? ''}
              onChange={(event) => onChange({ ...form, nip: event.target.value })}
              inputMode="numeric"
              placeholder="000-000-00-00"
              required
            />
            <button
              type="button"
              className="btn sec"
              disabled={(form.nip ?? '').replace(/[^0-9]/g, '').length !== 10 || fetching}
              onClick={onFetch}
            >
              {fetching ? t.companies.fetching : t.companies.fetch}
            </button>
          </div>
          <span className="hint">{fetched ?? t.counterparties.nipHint}</span>
        </div>
      )}
      <div className="field">
        <label htmlFor={id('Name')}>{t.counterparties.name}</label>
        <input
          id={id('Name')}
          className="inp"
          value={form.name}
          onChange={(event) => onChange({ ...form, name: event.target.value })}
          required
        />
      </div>
      {form.type === 'EU' && (
        <div className="field">
          <label htmlFor={id('VatEu')}>{t.counterparties.vatEuId}</label>
          <input
            id={id('VatEu')}
            className="inp mono"
            value={form.vatEuId ?? ''}
            onChange={(event) => onChange({ ...form, vatEuId: event.target.value })}
            placeholder="DE123456789"
            required
          />
        </div>
      )}
      {(form.type === 'EU' || form.type === 'NON_EU') && (
        <div className="field">
          <label htmlFor={id('Country')}>{t.counterparties.country}</label>
          <input
            id={id('Country')}
            className="inp mono"
            value={form.country}
            onChange={(event) => onChange({ ...form, country: event.target.value.toUpperCase() })}
            maxLength={2}
            placeholder="DE"
            required
          />
        </div>
      )}
      <div className="field">
        <label htmlFor={id('City')}>{t.counterparties.city}</label>
        <input
          id={id('City')}
          className="inp"
          value={form.city ?? ''}
          onChange={(event) => onChange({ ...form, city: event.target.value })}
        />
      </div>
    </div>
  );
}

/**
 * Pobranie danych z rejestru po NIP-ie. Wypełnia formularz, ale go nie zatwierdza —
 * to ten sam wykaz, z którego bierzemy dane przy zakładaniu firmy.
 */
export function useRegistryLookup(t: Dictionary) {
  const [fetched, setFetched] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  async function lookup(form: NewCounterparty): Promise<NewCounterparty | string> {
    if (!form.nip) return form;
    setFetching(true);
    setFetched(null);
    try {
      const found = await api.lookupCompany(form.nip);
      setFetched(
        found.vatStatus
          ? t.companies.fetchedWithVat(found.source, found.vatStatus)
          : t.companies.fetched(found.source),
      );
      return {
        ...form,
        nip: found.nip,
        name: found.name || form.name,
        addressLine: found.street ?? form.addressLine,
        postalCode: found.postalCode ?? form.postalCode,
        city: found.city ?? form.city,
      };
    } catch (failure) {
      return failure instanceof ApiError ? failure.message : String(failure);
    } finally {
      setFetching(false);
    }
  }

  return { fetched, setFetched, fetching, lookup };
}

export function CounterpartyDialog({
  companyId,
  t,
  onCreated,
  onClose,
}: {
  companyId: string;
  t: Dictionary;
  /** Utworzony kontrahent wraca w całości, żeby wywołujący mógł go od razu wybrać. */
  onCreated: (created: Counterparty) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<NewCounterparty>(emptyCounterparty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetched, setFetched, fetching, lookup } = useRegistryLookup(t);

  /*
   * `showModal` zamiast własnego „nakładka + div": daje pułapkę na fokus, zamykanie Escape
   * i tło blokujące kliknięcia — wszystko to, co w ręcznym okienku zwykle zostaje niezrobione.
   */
  useEffect(() => {
    ref.current?.showModal();
  }, []);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(await api.addCounterparty(companyId, form));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog className="dlg" ref={ref} onClose={onClose} aria-label={t.counterparties.add}>
      <form className="panel" onSubmit={save}>
        <div className="hd">
          <div>
            <h4>{t.counterparties.add}</h4>
            <p>{t.counterparties.dialogLead}</p>
          </div>
        </div>

        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}

        <CounterpartyFields
          form={form}
          onChange={(next) => {
            setForm(next);
            if (next.nip !== form.nip) setFetched(null);
          }}
          t={t}
          idPrefix="dlgK"
          fetched={fetched}
          fetching={fetching}
          onFetch={() => {
            void lookup(form).then((result) =>
              typeof result === 'string' ? setError(result) : setForm(result),
            );
          }}
        />

        <div className="bar2">
          <button type="submit" className="btn" disabled={busy || form.name.trim() === ''}>
            {busy ? t.companies.saving : t.counterparties.save}
          </button>
          <div className="sp">
            <button type="button" className="btn ghost" onClick={() => ref.current?.close()} disabled={busy}>
              {t.companies.cancel}
            </button>
          </div>
        </div>
      </form>
    </dialog>
  );
}
